<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import ZdhcService from '../../../services/Zdhc/ZdhcService';
import CatalogueProductList from './CatalogueProductList.vue';
import CatalogueProductCard from './CatalogueProductCard.vue';
import NoMatchButton from './NoMatchButton.vue';
import { associatedReferenceId, MATCH_STATUS } from '../../../utils/matchStatus';
/*
 * Il formato si controlla anche qui, non solo lato backend: la risposta a un
 * formato sbagliato e' sempre la stessa e non serve chiederla, cosi' l'utente
 * legge cosa c'e' di sbagliato subito invece di aspettare un giro di rete per un
 * "riprova" che non dipendeva dalla rete.
 */
import { REFERENCE_ID_PATTERN } from '../../../utils/referenceId';

/**
 * Associare un prodotto del catalogo al periodo.
 *
 * Una sezione sola per due stati che facevano la stessa cosa con due schermate
 * diverse: `action_required`, dove la ricerca automatica ha trovato piu' prodotti
 * compatibili e non ha saputo scegliere, e `no_match`, dove non ha trovato
 * niente. In entrambi i casi la domanda e' la stessa — quale prodotto e' questo —
 * e il campo di ricerca serve in entrambi: i candidati proposti possono non
 * contenere quello giusto.
 *
 * **La scelta e' in due tempi**: un clic seleziona, la conferma decide. Associare
 * al primo tocco rende un clic involontario indistinguibile da una decisione, e
 * qui la decisione cambia cosa finisce nel report verso ZDHC.
 *
 * **Se sia un match esatto o un manual match lo stabilisce il backend**, con lo
 * stesso criterio della risoluzione automatica: chi scegli decide quale prodotto
 * associare, non la natura dell'associazione.
 */

const emits = defineEmits(['associated', 'busy', 'close', 'newNoMatch', 'stale']);
const props = defineProps({
    product: { required: true },
    // Lo stato persistito della riga. Decide cosa si puo' dichiarare da qui: un
    // no-match si dichiara dove c'e' un'associazione da sciogliere o dei
    // candidati da scartare, non dove e' gia' stato dichiarato.
    productStatus: { required: false, default: null },
    // Il periodo e' chiuso da un report: lo dice l'elenco prodotti, che lo chiede
    // al backend una volta per periodo invece di dedurlo prodotto per prodotto.
    periodReported: { required: false, type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();
const { searchProducts, showProduct, associateMatch, associateSearchedMatch } = ZdhcService();

/** Il Gateway rifiuta le ricerche per nome sotto i tre caratteri. */
const NAME_MIN_LENGTH = 3;
/**
 * Quanti risultati per volta. Dieci stanno nello spazio che il modale ha, e chi
 * cerca guarda i primi: chiederne cinquanta per mostrarne dieci fa aspettare per
 * righe che nessuno leggera'.
 */
const PER_PAGE = 10;

const mode = ref('name');
const query = ref('');
const searching = ref(false);
const searched = ref(null);
const results = ref([]);
/**
 * Quanti risultati ha la ricerca **in tutto**, non quanti se ne vedono.
 *
 * Lo dice il catalogo (`meta.total`): con dieci righe per volta, contare quelle
 * a schermo diceva sempre "10" davanti a centinaia di prodotti, e sembrava che
 * la ricerca fosse gia' tutta li'. Quando il totale non arriva resta `null` e
 * si mostra quello che si sa: le righe visibili.
 */
const total = ref(null);
const page = ref(1);
const hasMore = ref(false);
const loadingMore = ref(false);

/**
 * Cosa e' stato selezionato **e da dove viene**.
 *
 * La provenienza non e' un dettaglio di presentazione: un candidato proposto e un
 * prodotto cercato si registrano su due endpoint diversi, perche' autorizzano
 * cose diverse — il primo accetta solo cio' che era in lista, il secondo vale
 * dove non c'e' niente di associato. Perdere la provenienza vorrebbe dire
 * indovinare quale dei due chiamare.
 */
const selection = ref(null);
const submitting = ref(false);
const failure = ref(null);

const candidates = computed( () => props.product.incheck_reference?.match_candidates ?? []);

/**
 * Il prodotto a cui questo periodo e' gia' associato.
 *
 * Aprendo da un match manuale la domanda non e' "quale prodotto e' questo" ma
 * "e' ancora quello giusto": senza vedere la scelta di prima si ricomincia da
 * zero, e si rischia di riassociare lo stesso prodotto credendo di cambiarlo.
 */
const associated = ref(null);
const loadingAssociated = ref(false);
const currentReferenceId = computed( () => associatedReferenceId(props.product.incheck_reference));

/**
 * C'e' un'associazione, quale che sia la sua forma.
 *
 * Non basta il Reference Id: i match ereditati, mai ri-agganciati al catalogo,
 * ne sono senza. Legandoci il riquadro, proprio le associazioni piu' vecchie —
 * quelle che piu' meritano di essere riviste — sarebbero sparite dalla vista.
 */
const hasAssociation = computed( () =>
{
    const reference = props.product.incheck_reference;

    return !!(reference?.zdhc_product_id || reference?.suggestion_id);
});

/**
 * Il no-match e' una dichiarazione sull'associazione: ha senso dove ce n'e' una
 * da sciogliere (un match manuale) o una da scartare (i candidati proposti).
 * Dove non c'e' niente — si e' aperto proprio da un no-match — non c'e' niente
 * da dichiarare, e al suo posto resta l'annullamento.
 */
const canDeclareNoMatch = computed( () =>
    props.productStatus !== MATCH_STATUS.NO_MATCH && props.productStatus !== MATCH_STATUS.PENDING);

const loadAssociated = async () =>
{
    if(!currentReferenceId.value) return;

    loadingAssociated.value = true;

    try
    {
        const res = await showProduct(currentReferenceId.value);

        // Se il catalogo non risponde resta il Reference Id, che e' comunque
        // l'identita' con cui il prodotto viaggia verso ZDHC: meglio di niente,
        // e non e' un motivo per non poter cercare.
        associated.value = res && !res.error && res.data
            ? res.data
            : { product_reference_id: currentReferenceId.value };
    }
    catch(e)
    {
        associated.value = { product_reference_id: currentReferenceId.value };
    }
    finally
    {
        loadingAssociated.value = false;
    }
};

loadAssociated();

/**
 * Contrassegno della ricerca corrente: una risposta che arriva dopo che la
 * domanda e' cambiata non ha piu' niente da dire.
 */
let currentSearch = 0;

const trimmed = computed( () => query.value.trim());
const canSearch = computed( () =>
    mode.value === 'reference_id'
        ? trimmed.value !== ''
        : trimmed.value.length >= NAME_MIN_LENGTH);

watch([mode, query], () =>
{
    currentSearch++;
    searching.value = false;
    loadingMore.value = false;
    results.value = [];
    total.value = null;
    searched.value = null;
    failure.value = null;
    page.value = 1;
    hasMore.value = false;
    clearSelectionIfSearched();
});

/**
 * Cambiando ricerca cade solo la selezione che veniva dai risultati: un candidato
 * proposto non c'entra con quello che si sta cercando, e farlo sparire
 * costringerebbe a ritrovarlo.
 */
function clearSelectionIfSearched()
{
    if(selection.value?.source !== 'search') return;

    selection.value = null;
}

/**
 * La chiave di una scelta e' provenienza **piu'** id: lo stesso prodotto puo'
 * comparire fra i proposti e fra i risultati, e le due copie non sono la stessa
 * scelta — si registrano su endpoint diversi. Confrontare il solo id avrebbe
 * evidenziato entrambe le righe e, cliccando l'altra, deselezionato invece di
 * cambiare provenienza.
 */
const keyOf = (product, source) => `${source}:${product.product_reference_id}`;

const select = (product, source) =>
{
    if(submitting.value) return;

    failure.value = null;
    selection.value = selectedKey.value === keyOf(product, source)
        ? null
        : { product, source };
};

const selectedKey = computed( () => selection.value
    ? keyOf(selection.value.product, selection.value.source)
    : null);
// Mentre si salva, la riga che si sta salvando e' l'unica accesa.
const submittingKey = computed( () => submitting.value ? selectedKey.value : null);

/**
 * Due modi di cercare la stessa cosa, e due endpoint: il nome e' una ricerca che
 * puo' dare molti risultati, il reference id e' un'identita' che ne da' uno o
 * nessuno.
 */
const search = async () =>
{
    if(!canSearch.value || searching.value || submitting.value) return;

    if(mode.value === 'reference_id' && !REFERENCE_ID_PATTERN.test(trimmed.value))
    {
        // Non e' una ricerca senza risultati: e' una domanda mal posta, e dirlo
        // e' l'unica risposta utile. `searched` resta vuoto, o comparirebbe anche
        // "nessun risultato per ..." su una ricerca che non e' partita.
        currentSearch++;
        results.value = [];
        total.value = null;
        searched.value = null;
        hasMore.value = false;
        failure.value = 'invalid_reference_id';

        return;
    }

    const run = ++currentSearch;
    searching.value = true;
    failure.value = null;
    results.value = [];
    total.value = null;
    page.value = 1;
    hasMore.value = false;
    clearSelectionIfSearched();
    // Si registra **cosa** e' stato cercato, non cosa c'e' nel campo: il messaggio
    // "nessun risultato per X" deve dire X anche se intanto si e' ricominciato a
    // digitare.
    searched.value = trimmed.value;

    try
    {
        const res = mode.value === 'reference_id'
            ? await showProduct(trimmed.value)
            : await searchProducts({ search: trimmed.value, page: 1, per_page: PER_PAGE });

        if(run !== currentSearch) return;

        if(!res || res.error)
        {
            // Un reference id inesistente e' una ricerca senza risultati, non un
            // guasto: e' la risposta alla domanda posta.
            if(res?.error === 'zdhc_not_found') return;

            // Il backend valida la forma del reference id con lo stesso schema:
            // se boccia qui, e' passato qualcosa che il controllo locale non
            // copriva, e vale la stessa spiegazione. **Solo in quel modo**: la
            // ricerca per nome ha le sue regole e viene bocciata con lo stesso
            // codice, e parlare del formato di un reference id che non c'entra
            // manderebbe a correggere la cosa sbagliata.
            failure.value = mode.value === 'reference_id' && res?.error === 'zdhc_validation'
                ? 'invalid_reference_id'
                : 'search';
            searched.value = null;

            return;
        }

        results.value = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
        // La ricerca per reference id non ha un totale da chiedere: e' un'identita',
        // e quello che torna e' tutto quello che c'e'.
        total.value = totalFrom(res) ?? (mode.value === 'reference_id' ? results.value.length : null);
        hasMore.value = mode.value === 'reference_id'
            ? false
            : moreAfter(res, { loaded: results.value.length, received: results.value.length });
    }
    catch(e)
    {
        if(run === currentSearch) failure.value = 'search';
    }
    finally
    {
        if(run === currentSearch) searching.value = false;
    }
};

/**
 * Il totale dichiarato dal catalogo.
 *
 * Si converte invece di pretendere un numero: il Gateway manda i numeri come
 * stringhe abbastanza spesso, e scartare `"247"` significherebbe ricadere sul
 * conteggio delle righe a schermo proprio quando il totale c'era. Un totale
 * assente o non convertibile non e' uno zero: e' un totale che non si sa.
 */
const totalFrom = (res) =>
{
    const declared = res?.meta?.total;

    if(declared === null || declared === undefined || declared === '') return null;

    const parsed = Number(declared);

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * C'e' un'altra pagina?
 *
 * Nell'ordine: l'ultima pagina dichiarata, poi il totale — se ne restano da
 * vedere, ce n'e' un'altra — e solo alla fine l'indizio della pagina piena. Il
 * solo indizio non bastava: una pagina che torna meno di dieci righe con un
 * totale di centinaia faceva sparire "Mostra altri" lasciando irraggiungibile
 * tutto il resto.
 *
 * I due numeri non sono intercambiabili: il totale si confronta con quante
 * righe si hanno **in tutto** (`loaded`), l'indizio guarda quante ne ha portate
 * **quest'ultima** risposta (`received`). Passare il cumulativo anche all'indizio
 * lasciava il pulsante accesso in eterno — venti righe restano un multiplo di
 * dieci anche dopo una pagina vuota.
 */
const moreAfter = (res, { loaded, received }) =>
{
    const lastPage = res?.meta?.last_page ?? null;

    if(lastPage !== null) return page.value < lastPage;

    // Il totale **conosciuto**, non quello di quest'ultima risposta: il catalogo
    // lo dichiara a intermittenza, e una pagina che non lo ripete non e' una
    // pagina che dice "non c'e' un totale". Leggendo solo la risposta corrente,
    // dopo una pagina corta il pulsante spariva a un terzo dei risultati mentre
    // l'intestazione ne annunciava tre volte tanti.
    const declared = total.value;

    if(declared !== null) return loaded < declared;

    return received > 0 && received % PER_PAGE === 0;
};

const loadMore = async () =>
{
    if(loadingMore.value || searching.value || submitting.value) return;

    const run = currentSearch;
    loadingMore.value = true;

    try
    {
        const res = await searchProducts({ search: searched.value, page: page.value + 1, per_page: PER_PAGE });

        if(run !== currentSearch) return;

        if(!res || res.error)
        {
            failure.value = 'search';

            return;
        }

        const rows = Array.isArray(res.data) ? res.data : [];

        page.value += 1;
        results.value = [...results.value, ...rows];
        total.value = totalFrom(res) ?? total.value;
        hasMore.value = moreAfter(res, { loaded: results.value.length, received: rows.length });
    }
    catch(e)
    {
        if(run === currentSearch) failure.value = 'search';
    }
    finally
    {
        if(run === currentSearch) loadingMore.value = false;
    }
};

const confirm = async () =>
{
    if(!selection.value || submitting.value) return;

    const { product, source } = selection.value;

    submitting.value = true;
    failure.value = null;
    emits('busy', true);

    try
    {
        // La provenienza decide l'endpoint: i due autorizzano cose diverse.
        const res = source === 'candidate'
            ? await associateMatch(props.product.incheck_reference.id, product.product_reference_id)
            : await associateSearchedMatch(props.product.incheck_reference.id, product.product_reference_id);

        if(res && !res.error && res.incheck_reference)
        {
            emits('associated', res.incheck_reference);

            return;
        }

        failure.value = res?.error ?? 'unknown';
    }
    catch(e)
    {
        failure.value = 'unknown';
    }
    finally
    {
        submitting.value = false;
        emits('busy', false);
    }
};

const cancel = () =>
{
    if(submitting.value) return;

    selection.value = null;
    emits('close');
};

/**
 * Ogni rifiuto ha un seguito diverso, e dire "riprova" a chi non deve riprovare
 * e' peggio che non dire niente.
 */
const failureMessage = computed( () =>
{
    if(!failure.value) return null;

    const known = [
        'search', 'no_choice_pending', 'not_a_candidate',
        'already_associated', 'period_already_reported', 'zdhc_not_found', 'invalid_reference_id',
    ];

    return t(`associationSection.errors.${known.includes(failure.value) ? failure.value : 'unknown'}`, store.state.locale);
});

/**
 * I due rifiuti che dicono "quello che vedi non e' piu' vero": riaprire non
 * basterebbe, il modale ricostruirebbe la lista dai dati locali.
 */
const isStaleFailure = computed( () => ['no_choice_pending', 'not_a_candidate'].includes(failure.value));

/**
 * Il totale non puo' essere meno di quello che si sta guardando: un catalogo che
 * dichiara `0` sopra dieci schede si contraddice, e fra le due la cosa vera e'
 * quella a schermo.
 */
const resultsTotal = computed( () => Math.max(total.value ?? 0, results.value.length));

/**
 * C'e' qualcosa da scegliere.
 *
 * I due pulsanti registrano una scelta fra queste righe: senza righe non
 * decidono niente, e mostrarli lascerebbe a schermo una conferma spenta sotto
 * un elenco vuoto.
 */
const hasChoices = computed( () => candidates.value.length > 0 || results.value.length > 0);

/**
 * Tre punti di partenza diversi: un'associazione gia' scelta a mano che si sta
 * rivedendo, dei candidati fra cui scegliere, o niente. La schermata e' la
 * stessa, la domanda no.
 */
const introMessage = computed( () =>
{
    if(hasAssociation.value) return t('associationSection.introAssociated', store.state.locale);

    return candidates.value.length
        ? t('associationSection.introWithCandidates', store.state.locale)
        : t('associationSection.intro', store.state.locale);
});

const showsEmptyResult = computed( () =>
    searched.value !== null && !searching.value && !results.value.length && !failure.value);
</script>
<template>
    <div class="w-full flex-1 min-h-0 flex flex-col space-y-4">
        <!-- Il periodo e' gia' stato dichiarato: il backend rifiuta, e mostrare
             una lista sarebbe promettere qualcosa che non si puo' fare. -->
        <p v-if="props.periodReported" class="text-sm font-semibold">
            {{ t('associationSection.errors.period_already_reported', store.state.locale) }}
        </p>

        <template v-else>
            <p class="text-sm">
                {{ introMessage }}
            </p>

            <!-- Sopra la ricerca, non dentro l'elenco: e' il punto di partenza —
                 cosa c'e' adesso — non una delle cose fra cui scegliere. -->
            <div
                v-if="hasAssociation"
                class="flex-shrink-0 flex flex-col space-y-1 border rounded-md p-3 bg-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <span class="text-sm font-semibold">
                    {{ t('associationSection.currentlyAssociated', store.state.locale) }}
                </span>
                <span v-if="loadingAssociated" class="text-sm" role="status">
                    {{ t('associationSection.loading', store.state.locale) }}
                </span>
                <CatalogueProductCard v-else-if="associated" :product="associated" />
                <span v-else class="text-sm">
                    {{ t('associationSection.associatedUnknown', store.state.locale) }}
                </span>
            </div>

            <!-- La ricerca resta fuori dallo scorrimento: e' il campo che serve
                 per riformulare, e sparire appena arrivano i risultati e' proprio
                 cio' che non deve fare. -->
            <form class="flex-shrink-0 flex flex-col space-y-3" @submit.prevent="search">
                <fieldset class="flex items-center space-x-4">
                    <legend class="sr-only">{{ t('associationSection.searchBy', store.state.locale) }}</legend>
                    <label class="flex items-center space-x-2 text-sm">
                        <input type="radio" name="search-mode" value="name" v-model="mode" :disabled="submitting">
                        <span>{{ t('productTable.cols.name', store.state.locale) }}</span>
                    </label>
                    <label class="flex items-center space-x-2 text-sm">
                        <input type="radio" name="search-mode" value="reference_id" v-model="mode" :disabled="submitting">
                        <span>Reference Id</span>
                    </label>
                </fieldset>

                <div class="flex items-start space-x-2">
                    <div class="flex flex-col flex-grow">
                        <label for="association-search" class="sr-only">
                            {{ t('associationSection.searchBy', store.state.locale) }}
                        </label>
                        <input
                            id="association-search"
                            v-model="query"
                            type="text"
                            autocomplete="off"
                            :disabled="submitting"
                            :placeholder="mode === 'reference_id' ? '20-XXXXXXXXXXXX-X' : t('product.search', store.state.locale)"
                            :aria-invalid="failure === 'invalid_reference_id' ? 'true' : null"
                            :aria-describedby="failure === 'invalid_reference_id'
                                ? 'association-search-hint association-search-error'
                                : 'association-search-hint'"
                            class="h-12 px-4 border rounded shadow-sm transition duration-200 bg-white focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:focus:border-blue-400">
                        <!-- La soglia la impone il Gateway: dirla prima e' meglio
                             che mandare una richiesta che sara' rifiutata. -->
                        <span id="association-search-hint" class="text-sm pt-1" aria-live="polite">
                            <template v-if="mode === 'name' && trimmed.length > 0 && trimmed.length < NAME_MIN_LENGTH">
                                {{ t('associationSection.minLength', store.state.locale) }}
                            </template>
                            <!-- Il formato atteso sta nel segnaposto, che pero' e'
                                 coperto dal valore sbagliato proprio quando
                                 servirebbe: il messaggio manda a verificarlo, e
                                 qui si puo' vedere. -->
                            <template v-else-if="failure === 'invalid_reference_id'">
                                {{ t('associationSection.referenceIdFormat', store.state.locale) }}: 20-XXXXXXXXXXXX-X
                            </template>
                        </span>
                    </div>
                    <button
                        type="submit"
                        class="h-12 px-5 rounded bg-blue-500 text-white duration-300 disabled:opacity-50"
                        :disabled="!canSearch || searching || submitting">
                        {{ t('actions.search', store.state.locale) }}
                    </button>
                </div>
            </form>

            <!-- Un solo scorrimento per le due liste: sono lo stesso elenco di
                 cose fra cui scegliere, e due aree che scorrono separatamente
                 farebbero perdere il conto. -->
            <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col space-y-4">
                <div v-if="candidates.length" class="flex flex-col space-y-2">
                    <h3 class="text-sm font-semibold">
                        {{ t('associationSection.proposed', store.state.locale) }}
                    </h3>
                    <CatalogueProductList
                        :products="candidates"
                        source="candidate"
                        :selected="selectedKey"
                        :submitting="submittingKey"
                        selectable
                        @choose="product => select(product, 'candidate')" />
                </div>

                <div v-if="searching" class="flex items-center justify-center py-4" role="status" :aria-label="t('actions.search', store.state.locale)">
                    <svg class="animate-spin w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                </div>

                <div v-else-if="results.length" class="flex flex-col space-y-2">
                    <h3 class="text-sm font-semibold" role="status">
                        {{ t('associationSection.resultsFound', store.state.locale) }}: {{ resultsTotal }}
                    </h3>
                    <CatalogueProductList
                        :products="results"
                        source="search"
                        :selected="selectedKey"
                        :submitting="submittingKey"
                        selectable
                        @choose="product => select(product, 'search')" />
                    <div v-if="hasMore" class="flex items-center justify-center pt-1">
                        <button
                            class="px-4 py-2 rounded border duration-300 hover:border-blue-500 disabled:opacity-50"
                            :disabled="loadingMore || submitting"
                            @click.prevent="loadMore">
                            {{ loadingMore
                                ? t('associationSection.loading', store.state.locale)
                                : t('associationSection.loadMore', store.state.locale) }}
                        </button>
                    </div>
                </div>

                <p v-else-if="showsEmptyResult" class="text-sm font-semibold" role="status">
                    {{ t('associationSection.noResults', store.state.locale) }} "{{ searched }}"
                </p>
            </div>

            <!-- Ha un id perche' il campo possa dichiararlo suo: un avviso
                 annunciato una volta e poi slegato dall'input non si ritrova
                 tornando sul campo per correggere. -->
            <p v-if="failureMessage" id="association-search-error" class="flex-shrink-0 text-sm text-red-500" role="alert">
                {{ failureMessage }}
            </p>

            <div v-if="isStaleFailure" class="flex-shrink-0 flex items-center justify-end">
                <button
                    class="px-4 py-2 rounded border duration-300 hover:border-blue-500"
                    @click.prevent="emits('stale')">
                    {{ t('associationSection.refresh', store.state.locale) }}
                </button>
            </div>

            <!-- Fuori dallo scorrimento e in fondo: la decisione si prende dopo
                 aver scorso, e chi la registra deve stare dove si finisce di
                 guardare. Senza righe fra cui scegliere non c'e' niente da
                 decidere: si chiude dalla X, come ogni altro modale. -->
            <div v-if="hasChoices || canDeclareNoMatch" class="flex-shrink-0 flex items-center justify-end space-x-3 pt-3 border-t dark:border-gray-700">
                <!-- Dove c'e' un'associazione da sciogliere o dei candidati da
                     scartare, dichiarare "nessuna corrispondenza" e' l'altra
                     uscita possibile, e prende il posto dell'annullamento: sono
                     due modi di dire "non e' nessuno di questi", uno dei quali
                     lascia detto anche al report. -->
                <NoMatchButton
                    v-if="canDeclareNoMatch"
                    :product="props.product"
                    @busy="value => emits('busy', value)"
                    @newNoMatch="value => emits('newNoMatch', value)" />
                <button
                    v-else
                    class="px-4 py-2 rounded border duration-300 hover:border-blue-500 disabled:opacity-50"
                    :disabled="submitting"
                    @click.prevent="cancel">
                    {{ t('actions.cancel', store.state.locale) }}
                </button>
                <button
                    v-if="hasChoices"
                    class="px-5 py-2 rounded bg-blue-500 text-white duration-300 disabled:opacity-50"
                    :disabled="!selection || submitting"
                    @click.prevent="confirm">
                    {{ submitting
                        ? t('associationSection.loading', store.state.locale)
                        : t('actions.confirm', store.state.locale) }}
                </button>
            </div>
        </template>
    </div>
</template>
